import {useDispatch} from 'react-redux';
import axios from '../utils/axiosConfig';
import {
    populateChatsFromHistory,
    setChatHistoryList,
    appendChatHistoryList,
    setChatHistoryListError,
    setChatHistoryListLoading,
    setChatMessageById,
    setChatMessageByIdError,
    setChatMessageByIdLoading,
    setChatMessages,
    setChatMessagesError,
    setChatMessagesLoading,
    setChatSubquestions,
    setChatSubquestionsError,
    setChatSubquestionsLoading,
    setFetchedChatWithMessages,
    setLastActiveChatId,
    setNewChatIdLoading,
    setPinnedChatsLoading,
    setPinnedChatsError,
    setPinnedChats,
    setChatShareLoading,
    setChatShareError,
    setChatShare,
    setHasMoreChats,
    setIsLoadingMore,
    setCurrentPage
} from '../redux/slices/chat';
import {
    ChatHistoryByDate,
    HandleSubquestionResponse,
    HandleSubquestionsRequest
} from "../components/GlobalChatBox/types";
import {
    MentionSearchType,
    MentionSearchResponse
} from "../components/GlobalChatBox/types/mentionSearch";
import moment from "moment";
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

axios.interceptors.request.use(async function (config:any) {
    let  token = ''
    if(Clerk?.session ){
        token =  await Clerk.session.getToken({template:"main"});
    }
    config.headers.Authorization = `Bearer ${token}`
    return config;
});

axios.defaults.timeout = 60000;

const useChatService = () => {
    const dispatch = useDispatch();

    const handleChatQuery = async (body: any) => {
        dispatch(setChatMessagesLoading(true));
        try {
            const response = await axios.post<any>(`${API_BASE_URL}/handle-query`,body);
            dispatch(setChatMessages(response.data));
        } catch (error) {
            dispatch(setChatMessagesError(error));
        } finally {
            dispatch(setChatMessagesLoading(false));
        }
    };

    const handleoldChatSubquestions = async (body: any) => {
        dispatch(setChatSubquestionsLoading(true));
        try {
            const response = await axios.post<any>(`${API_BASE_URL}/old_handle_subquestions`,body);
            dispatch(setChatSubquestions(response?.data?.sub_questions));
        } catch (error) {
            dispatch(setChatSubquestionsError(error));
        } finally {
            dispatch(setChatSubquestionsLoading(false));
        }
    };

    const getPinnedChats = async () => {
        dispatch(setPinnedChatsLoading(true));
        try {
            const response = await axios.get<any>(`${API_BASE_URL}/chat/pinned`);
            dispatch(setPinnedChats(response?.data || []));
        } catch (error) {
            dispatch(setPinnedChatsError(error));
        } finally {
            dispatch(setPinnedChatsLoading(false));
        }
    };

    const togglePinChat = async (chat_id: number | string, is_pinned: boolean) => {
        try {
            const response = await axios.post<any>(`${API_BASE_URL}/chat/${chat_id}/pin`, { is_pinned });
            return response.data;
        } catch (error) {
            console.error('Failed to toggle pin chat:', error);
            throw error;
        }
    };

    const handleChatSubquestions = async (body: any) => {
        dispatch(setChatSubquestionsLoading(true));
        try {
            // Transform the body parameters to match the API expectation
            const apiBody: HandleSubquestionsRequest = {
                entity: body.entity || body.context_type || 'work_unit',
                entity_id: body.entity_id || body.project_id || '',
                isnew: body.isnew || body.isnewproject === 'true' || false,
                is_first_time: body.is_first_time !== undefined ? body.is_first_time : true
            };
            
            // Include entity_name if provided
            if (body.entity_name !== undefined) {
                apiBody.entity_name = body.entity_name;
            }
            
            const response = await axios.post<HandleSubquestionResponse>(`${API_BASE_URL}/handle-subquestions`, apiBody);
            dispatch(setChatSubquestions(response?.data?.sub_questions));
        } catch (error) {
            dispatch(setChatSubquestionsError(error));
        } finally {
            dispatch(setChatSubquestionsLoading(false));
        }
    };
    const getLastActiveQuestions = async (project_id: any) => {
        // dispatch(setChatLastActiveQuestionsLoading(true));
        try {
            // Updated API endpoint to include orgId
            const response = await axios.get<any>(`${API_BASE_URL}/chat/lastActiveChat`);
            // dispatch(setLastActiveQuestions(response?.data?.data));
            dispatch(setLastActiveChatId(response?.data?.chat_id));
        } catch (error) {
            // dispatch(setChatLastActiveQuestionsError(error));
        } finally {
            // dispatch(setChatLastActiveQuestionsLoading(false));
        }
    };

    const getoldLastActiveQuestions = async (project_id: any) => {
        // dispatch(setChatLastActiveQuestionsLoading(true));
        try {
            const response = await axios.get<any>(`${API_BASE_URL}/chat/project/${project_id}/lastActiveChat`);
            // dispatch(setLastActiveQuestions(response?.data?.data));
            dispatch(setLastActiveChatId(response?.data?.chat_id));
        } catch (error) {
            // dispatch(setChatLastActiveQuestionsError(error));
        } finally {
            // dispatch(setChatLastActiveQuestionsLoading(false));
        }
    };

    const getChatHistoryList = async (project_id: any, options: { page?: number, per_page?: number, search?: string, append?: boolean } = {}) => {
        const { page = 1, per_page = 20, search = '', append = false } = options;
        
        if (append) {
            dispatch(setIsLoadingMore(true));
        } else {
            dispatch(setChatHistoryListLoading(true));
        }

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const now = moment().tz(timezone);
        const microseconds = now.millisecond().toString().padStart(3, '0') + '456';
        const pythonLikeTime = `${now.format('YYYY-MM-DD HH:mm:ss')}.${microseconds}`;

        try {
            const params: any = {
                page,
                per_page
            };
            
            if (search.trim()) {
                params.search = search.trim();
            }

            const response = await axios.get<any>(`${API_BASE_URL}/chat`, {
                headers: {
                    'X-Client-Time': pythonLikeTime,
                    'X-Client-Timezone': timezone
                },
                params
            });
            
            const chatData = response?.data;
            
            const totalCurrentItems = (chatData.today?.length || 0) + 
                                    (chatData.yesterday?.length || 0) + 
                                    (chatData.older?.length || 0);
                

            const hasMore = totalCurrentItems === per_page;
            dispatch(setHasMoreChats(hasMore));
            
            if (append) {
                dispatch(appendChatHistoryList(chatData));
                dispatch(setCurrentPage(page));
            } else {
                dispatch(setChatHistoryList(chatData));
                dispatch(setCurrentPage(1));
            }
            dispatch(populateChatsFromHistory(chatData));
            
        } catch (error) {
            dispatch(setChatHistoryListError(error));
            if (append) {
                dispatch(setHasMoreChats(false));
            }
        } finally {
            dispatch(setChatHistoryListLoading(false));
            dispatch(setIsLoadingMore(false));
        }
    };

    const getOldChatHistoryList = async (project_id: any) => {
        dispatch(setChatHistoryListLoading(true));
        try {
            // Updated API endpoint to include orgId
            const response = await axios.get<ChatHistoryByDate>(`${API_BASE_URL}/chat/project/${project_id}`);
            dispatch(setChatHistoryList(response?.data));
        } catch (error) {
            dispatch(setChatHistoryListError(error));
        } finally {
            dispatch(setChatHistoryListLoading(false));
        }
    };
    const getChatMessageById = async (chat_id: any) => {
        dispatch(setChatMessageByIdLoading(true));
        try {
            const response = await axios.get<any>(`${API_BASE_URL}/chat/${chat_id}`);
            dispatch(setChatMessageById(response?.data));
            dispatch(setFetchedChatWithMessages({
                chatId: chat_id,
                messages: response?.data
            }));
            dispatch(setLastActiveChatId(chat_id));
        } catch (error) {
            dispatch(setChatMessageByIdError(error));
        } finally {
            dispatch(setChatMessageByIdLoading(false));
        }
    };
    const getNewChatId = async (body: any) => {
        dispatch(setNewChatIdLoading(true));
        try {
            const response = await axios.post<any>(`${API_BASE_URL}/newchat`,body);
            dispatch(setLastActiveChatId(response?.data));
        } catch (error) {
            // dispatch(setChatSubquestionsError(error));
        } finally {
            dispatch(setNewChatIdLoading(false));
        }
    }
    const deleteChatMessageById = async (chat_id: any,project_id:string) => {
        dispatch(setChatMessageByIdLoading(true));
        try {
            const response = await axios.delete<any>(`${API_BASE_URL}/chat/${chat_id}`);
            toast.success(response.data.message);
        } catch (error) {
            dispatch(setChatMessageByIdError(error));
        } finally {
            dispatch(setChatMessageByIdLoading(false));
        }
    };

    const saveChatTitleById = async (chat_id: any,payload:any) => {
        try {
            const response = await axios.put<any>(`${API_BASE_URL}/chat/${chat_id}`,payload);
            // notify.show(response.data.message, "success", 3000);
        } catch (error) {
            dispatch(setChatMessageByIdError(error));
        }
    };

    const searchProjectsRoadmapsTeamsResources = async (
        searchTerm: string = '', 
        searchType: MentionSearchType = MentionSearchType.ALL,
        page: number = 1, 
        perPage: number = 20
    ): Promise<MentionSearchResponse> => {
        try {
            const response = await axios.get<MentionSearchResponse>(
              `${API_BASE_URL}/search/strategymaps-epic-feature-teams-resources`,
              {
                params: {
                  search_term: searchTerm,
                  search_type: searchType,
                  page: page,
                  per_page: perPage,
                },
              },
            );
            return response.data;
        } catch (error) {
            console.error('Error searching projects, roadmaps, teams, and resources:', error);
            throw error;
        }
    };

    const searchChatHistory = async (searchTerm: string = '', page: number = 1, perPage: number = 20, append: boolean = false) => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const now = moment().tz(timezone);
        const microseconds = now.millisecond().toString().padStart(3, '0') + '456';
        const pythonLikeTime = `${now.format('YYYY-MM-DD HH:mm:ss')}.${microseconds}`;

        try {
            const response = await axios.get<any>(`${API_BASE_URL}/chat`, {
                headers: {
                    'X-Client-Time': pythonLikeTime,
                    'X-Client-Timezone': timezone
                },
                params: {
                    page,
                    per_page: perPage,
                    search: searchTerm.trim()
                }
            });
            
            const chatData = response.data;
            
            const totalCurrentItems = (chatData.today?.length || 0) + 
                                    (chatData.yesterday?.length || 0) + 
                                    (chatData.older?.length || 0);
            
            return {
                ...chatData,
                hasMore: totalCurrentItems === perPage,
                totalItems: totalCurrentItems
            };
        } catch (error) {
            console.error('Search error:', error);
            throw error;
        }
    };

    // New SSE methods
    const createChatSession = async (body: any) => {
        try {
            const response = await axios.post<any>(`${API_BASE_URL}/chat/session/create`, body);
            return response.data;
        } catch (error) {
            console.error('Failed to create chat session:', error);
            throw error;
        }
    };

    const sendMessageToSession = async (sessionId: string, messageData: string | any) => {
        try {
            // Handle both string (legacy) and object (with context) formats
            const payload = typeof messageData === 'string' 
                ? { text: messageData }
                : { text: messageData.text, ...messageData };
            
            const response = await axios.post<any>(`${API_BASE_URL}/chat/session/${sessionId}/send`, payload);
            return response.data;
        } catch (error) {
            console.error('Failed to send message:', error);
            throw error;
        }
    };

    const cancelMessageGeneration = async (sessionId: string) => {
        try {
            const response = await axios.post<any>(`${API_BASE_URL}/chat/session/${sessionId}/cancel`);
            return response.data;
        } catch (error) {
            console.error('Failed to cancel message generation:', error);
            throw error;
        }
    };

    const deleteSession = async (sessionId: string) => {
        try {
            const response = await axios.delete<any>(`${API_BASE_URL}/chat/session/${sessionId}`);
            return response.data;
        } catch (error) {
            console.error('Failed to delete session:', error);
            throw error;
        }
    };

    const sendMessageFeedback = async (chat_id: number | string, message_id: number | string, feedback_is_positive: boolean) => {
        try {
            const response = await axios.put<any>(
                `${API_BASE_URL}/chat/${chat_id}/message/${message_id}/feedback`,
                { feedback_is_positive }
            );
            return response.data;
        } catch (error) {
            console.error('Failed to send message feedback:', error);
            throw error;
        }
    };

    const respondToInterrupt = async (sessionId: string, interruptId: string, responseType: string, responseData: any, chatId?: string | number, messageId?: number) => {
        try {
            const response = await axios.post<any>(
                `${API_BASE_URL}/chat/session/${sessionId}/interrupt/respond`,
                {
                    interrupt_id: interruptId,
                    response_type: responseType,
                    response_data: responseData,
                    chat_id: chatId,
                    message_id: messageId
                }
            );
            return response.data;
        } catch (error) {
            console.error('Failed to respond to interrupt:', error);
            throw error;
        }
    };

    const updateChatShareStatus = async (chat_id: number | string, is_shared: boolean) => {
        try {
            const response = await axios.post<any>(`${API_BASE_URL}/chat/${chat_id}/share`, { is_shared });
            toast.success(response.data.message);
            
            // Set share data based on response
            if (is_shared && response.data.share_token) {
                dispatch(setChatShare({ token: response.data.share_token }));
            } else {
                // Clear share data when disabling
                dispatch(setChatShare(null));
            }
            
            return response.data;
        } catch (error) {
            console.error('Failed to update chat share status:', error);
            toast.error('Failed to update share settings');
            throw error;
        }
    };

    const getSharedChat = async (token: string) => {
        dispatch(setChatShareLoading(true));
        try {
            const response = await axios.post<any>(`${API_BASE_URL}/chat/share`, { token }, {
                ignoreGlobalCatch: true,
            } as any);
            return response.data;
        } catch (error) {
            console.error('Failed to get shared chat:', error);
            dispatch(setChatShareError(error));
            throw error;
        } finally {
            dispatch(setChatShareLoading(false));
        }
    };

    return { 
            handleChatQuery,
            handleChatSubquestions,
            getLastActiveQuestions,
            getChatHistoryList,
            getChatMessageById,
            getNewChatId, 
            deleteChatMessageById,
            saveChatTitleById, 
            getoldLastActiveQuestions,
            searchProjectsRoadmapsTeamsResources,
            searchChatHistory,
            // New SSE methods
            createChatSession,
            sendMessageToSession,
            cancelMessageGeneration,
            deleteSession,
            sendMessageFeedback,
            getOldChatHistoryList,
            handleoldChatSubquestions,
            respondToInterrupt,
            getPinnedChats,
            togglePinChat,
            updateChatShareStatus,
            getSharedChat
        };
}

export default useChatService;